import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Button, CircularProgress, Dialog, IconButton, Snackbar, Typography } from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import IosShareRoundedIcon from "@mui/icons-material/IosShareRounded";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebaseConfig";
import { PHOTO_WEEKS } from "../content/matchPhotos";
import { supabase } from "../supabaseClient";
import { EmptyState, PillChips } from "./ui/Pills";
import { FadeSwitch } from "./ui/motion";

const BUCKET = "match-photos";
const publicUrl = (path) => supabase.storage.from(BUCKET).getPublicUrl(path).data.publicUrl;
const photoUrl = (week, album, name) => publicUrl(`${week.path}/${album.key}/${name}.jpg`);
const thumbUrl = (week, album, name) => publicUrl(`${week.path}/${album.key}/thumbs/${name}.webp`);

const track = (name, params) => {
  try {
    logEvent(analytics, name, params);
  } catch {
    // Analytics is best-effort.
  }
};

/** Image that retries a couple of times before giving up, so a dropped request doesn't leave a blank tile. */
const RetryImage = ({ src, ...props }) => {
  const [attempt, setAttempt] = useState(0);
  useEffect(() => setAttempt(0), [src]);
  return (
    <Box
      component="img"
      src={attempt ? `${src}?retry=${attempt}` : src}
      onError={() => attempt < 2 && setTimeout(() => setAttempt((a) => a + 1), 800 * (attempt + 1))}
      {...props}
    />
  );
};

const FilterLabel = ({ children }) => (
  <Typography
    component="span"
    sx={{ fontSize: "0.68rem", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(0,0,0,0.45)", minWidth: 64 }}
  >
    {children}
  </Typography>
);

const pillButtonSx = (dark) => ({
  textTransform: "none",
  fontWeight: 800,
  borderRadius: 999,
  px: 1.75,
  color: dark ? "#fff" : "#111",
  bgcolor: dark ? "rgba(255,255,255,0.14)" : "#f4f4f5",
  border: dark ? "1px solid rgba(255,255,255,0.28)" : "1px solid #e4e4e7",
  "&:hover": { bgcolor: dark ? "rgba(255,255,255,0.24)" : "#e4e4e7" },
  "&.Mui-disabled": { color: dark ? "rgba(255,255,255,0.6)" : "#888" },
});

const photoLink = (week, album, name) => {
  const qs = new URLSearchParams({ week: String(week.week), album: album.key });
  if (name) qs.set("photo", name);
  return `${window.location.origin}/photos?${qs}`;
};

async function fetchPhotoFile(week, album, name) {
  const res = await fetch(photoUrl(week, album, name));
  if (!res.ok) throw new Error(`Photo request failed: ${res.status}`);
  const blob = await res.blob();
  return new File([blob], `HSL-${album.key}-${name}.jpg`, { type: "image/jpeg" });
}

function saveFile(file) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(file);
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
}

/**
 * Shares the photo itself where the browser supports file sharing, otherwise a link to it.
 * Safari drops the tap's permission to share if fetching the file takes too long; that
 * returns "retry" so the next tap can share the already-fetched file straight away.
 */
async function sharePhoto({ week, album, name, getFile }) {
  const url = photoLink(week, album, name);
  const text = `${album.division} · ${week.label} photos`;
  try {
    const file = await getFile();
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], text, url });
      return "shared";
    }
  } catch (e) {
    if (e?.name === "AbortError") return "cancelled";
    if (e?.name === "NotAllowedError") return "retry";
  }
  if (navigator.share) {
    try {
      await navigator.share({ title: text, url });
      return "shared";
    } catch (e) {
      if (e?.name === "AbortError") return "cancelled";
    }
  }
  try {
    await navigator.clipboard.writeText(url);
    return "copied";
  } catch {
    return "failed";
  }
}

const Viewer = ({ week, album, index, onIndex, onClose }) => {
  const photos = album.photos;
  const name = photos[index];
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(null);
  const [notice, setNotice] = useState("");
  const touchX = useRef(null);
  const prepared = useRef(null);

  const go = useCallback((step) => onIndex((index + step + photos.length) % photos.length), [index, onIndex, photos.length]);

  useEffect(() => setLoaded(false), [name]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft") go(-1);
      if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go]);

  useEffect(() => {
    [index - 1, index + 1].forEach((i) => {
      const n = photos[(i + photos.length) % photos.length];
      if (n) new Image().src = photoUrl(week, album, n);
    });
  }, [index, photos, week, album]);

  if (!name) return null;

  const getFile = async () => {
    if (prepared.current?.name === name) return prepared.current.file;
    const file = await fetchPhotoFile(week, album, name);
    prepared.current = { name, file };
    return file;
  };

  const onDownload = async () => {
    setBusy("download");
    try {
      saveFile(await getFile());
      track("photo_downloaded", { album: album.key, week: week.week });
    } catch (e) {
      console.error("Photo download:", e);
      setNotice("Couldn't download this photo, try again");
    } finally {
      setBusy(null);
    }
  };

  const onShare = async () => {
    setBusy("share");
    const outcome = await sharePhoto({ week, album, name, getFile });
    setBusy(null);
    if (outcome === "retry") setNotice("Photo ready, tap Share again");
    if (outcome === "copied") setNotice("Link copied");
    if (outcome === "failed") setNotice("Couldn't share this photo");
    if (outcome === "shared") track("photo_shared", { album: album.key, week: week.week });
  };

  const navButtonSx = {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#fff",
    bgcolor: "rgba(0,0,0,0.45)",
    "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
    display: { xs: "none", sm: "inline-flex" },
  };

  return (
    <Dialog fullScreen open onClose={onClose} PaperProps={{ sx: { bgcolor: "#000", color: "#fff" } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: { xs: 1.5, sm: 2.5 }, py: 1.25, borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ fontWeight: 900, fontSize: "0.95rem" }}>
            {album.division} · {week.label}
          </Typography>
          <Typography sx={{ fontSize: "0.75rem", color: "rgba(255,255,255,0.6)", fontVariantNumeric: "tabular-nums" }}>
            {index + 1} of {photos.length}
          </Typography>
        </Box>
        <Button
          onClick={onDownload}
          disabled={!!busy}
          size="small"
          startIcon={busy === "download" ? <CircularProgress size={14} color="inherit" /> : <DownloadRoundedIcon sx={{ fontSize: 18 }} />}
          sx={pillButtonSx(true)}
        >
          Download
        </Button>
        <Button
          onClick={onShare}
          disabled={!!busy}
          size="small"
          startIcon={busy === "share" ? <CircularProgress size={14} color="inherit" /> : <IosShareRoundedIcon sx={{ fontSize: 18 }} />}
          sx={pillButtonSx(true)}
        >
          Share
        </Button>
        <IconButton onClick={onClose} aria-label="Close" sx={{ color: "#fff", ml: 0.5 }}>
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      <Box
        sx={{ position: "relative", flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center", p: { xs: 0, sm: 2 } }}
        onTouchStart={(e) => {
          touchX.current = e.touches[0].clientX;
        }}
        onTouchEnd={(e) => {
          if (touchX.current == null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          touchX.current = null;
          if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
        }}
      >
        {!loaded && <CircularProgress sx={{ position: "absolute", color: "rgba(255,255,255,0.7)" }} />}
        <RetryImage
          key={name}
          src={photoUrl(week, album, name)}
          alt={`${album.division} ${week.label} photo ${index + 1}`}
          onLoad={() => setLoaded(true)}
          sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block", opacity: loaded ? 1 : 0, transition: "opacity 200ms ease" }}
        />
        <IconButton aria-label="Previous photo" onClick={() => go(-1)} sx={{ ...navButtonSx, left: 16 }}>
          <ChevronLeftRoundedIcon fontSize="large" />
        </IconButton>
        <IconButton aria-label="Next photo" onClick={() => go(1)} sx={{ ...navButtonSx, right: 16 }}>
          <ChevronRightRoundedIcon fontSize="large" />
        </IconButton>
      </Box>
      <Typography sx={{ textAlign: "center", fontSize: "0.75rem", color: "rgba(255,255,255,0.5)", py: 1, display: { xs: "block", sm: "none" } }}>
        Swipe for more
      </Typography>
      <Snackbar open={!!notice} autoHideDuration={2500} onClose={() => setNotice("")} message={notice} />
    </Dialog>
  );
};

/** Match-day photos by week and division, viewable, downloadable and shareable one at a time. */
const Photos = () => {
  const [params, setParams] = useSearchParams();
  const [notice, setNotice] = useState("");

  const week = PHOTO_WEEKS.find((w) => String(w.week) === params.get("week")) || PHOTO_WEEKS[0];
  const requestedAlbum = (params.get("album") || "").toLowerCase();
  const album =
    week?.albums.find((a) => a.key === requestedAlbum || a.division.toLowerCase() === requestedAlbum) || week?.albums[0];
  const photoName = params.get("photo");
  const openIndex = useMemo(() => (album && photoName ? album.photos.indexOf(photoName) : -1), [album, photoName]);
  const total = week ? week.albums.reduce((n, a) => n + a.photos.length, 0) : 0;

  const update = (next, { replace = true } = {}) => {
    const qs = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => (v == null ? qs.delete(k) : qs.set(k, v)));
    setParams(qs, { replace });
  };

  if (!week || !album) {
    return (
      <Box sx={{ maxWidth: 1200, mx: "auto", minHeight: "70vh", pt: 3 }}>
        <EmptyState title="No photos yet" body="Match-day photos show up here after each week's games." />
      </Box>
    );
  }

  const shareAlbum = async () => {
    const url = photoLink(week, album);
    const title = `${album.division} · ${week.label} photos`;
    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      setNotice("Album link copied");
    } catch (e) {
      if (e?.name !== "AbortError") setNotice("Couldn't share the album");
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", minHeight: "70vh", pb: 4 }}>
      <Box component="header" sx={{ px: { xs: 2, sm: 3 }, pt: 3 }}>
        <Typography sx={{ fontSize: "0.75rem", fontWeight: 800, letterSpacing: 2, textTransform: "uppercase", color: "rgba(0,0,0,0.45)" }}>
          HSL · {week.season}
        </Typography>
        <Typography component="h1" sx={{ fontSize: { xs: "1.9rem", sm: "2.5rem" }, fontWeight: 900, letterSpacing: "-0.03em", lineHeight: 1.1, mt: 0.25, color: "#111" }}>
          Match photos
        </Typography>
        <Typography sx={{ color: "rgba(0,0,0,0.55)", fontSize: "0.85rem", mt: 0.5 }}>
          {week.label} · {total} photos. Tap a photo to view it full size, download it or share it.
        </Typography>

        <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <FilterLabel>Week</FilterLabel>
            <PillChips
              ariaLabel="Week"
              value={String(week.week)}
              onChange={(w) => update({ week: w, album: null, photo: null })}
              options={PHOTO_WEEKS.map((w) => ({ value: String(w.week), label: w.label }))}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <FilterLabel>Division</FilterLabel>
            <PillChips
              ariaLabel="Division"
              value={album.key}
              onChange={(key) => update({ album: key, photo: null })}
              options={week.albums.map((a) => ({ value: a.key, label: `${a.division} · ${a.photos.length}` }))}
            />
          </Box>
        </Box>

        <Box sx={{ mt: 2.5, pb: 2, display: "flex", gap: 1, flexWrap: "wrap", borderBottom: "1px solid rgba(0,0,0,0.08)" }}>
          <Button size="small" startIcon={<IosShareRoundedIcon sx={{ fontSize: 18 }} />} onClick={shareAlbum} sx={pillButtonSx(false)}>
            Share album
          </Button>
        </Box>
      </Box>

      <FadeSwitch motionKey={`${week.week}|${album.key}`}>
        <Box
          component="ul"
          sx={{
            listStyle: "none",
            m: 0,
            px: { xs: 0.5, sm: 3 },
            pt: 2,
            display: "grid",
            gap: { xs: 0.5, sm: 1 },
            gridTemplateColumns: { xs: "repeat(3, 1fr)", sm: "repeat(auto-fill, minmax(180px, 1fr))" },
          }}
        >
          {album.photos.map((name, i) => (
            <Box component="li" key={name} sx={{ aspectRatio: "1 / 1", bgcolor: "#ececec", borderRadius: { xs: 0.5, sm: 1.5 }, overflow: "hidden" }}>
              <Box
                component="button"
                type="button"
                onClick={() => update({ photo: name }, { replace: false })}
                aria-label={`Open photo ${i + 1} of ${album.photos.length}`}
                sx={{ p: 0, m: 0, border: 0, width: "100%", height: "100%", cursor: "pointer", display: "block", bgcolor: "transparent" }}
              >
                <RetryImage
                  src={thumbUrl(week, album, name)}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block", transition: "transform 200ms ease", "&:hover": { transform: "scale(1.03)" } }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </FadeSwitch>

      {openIndex >= 0 && (
        <Viewer
          week={week}
          album={album}
          index={openIndex}
          onIndex={(i) => update({ photo: album.photos[i] })}
          onClose={() => update({ photo: null })}
        />
      )}
      <Snackbar open={!!notice} autoHideDuration={2500} onClose={() => setNotice("")} message={notice} />
    </Box>
  );
};

export default Photos;
